import { scoringService } from './scoring';
import { feedService } from './feed';
import { authService } from './auth';
import * as db from './db';

const QUIZ_KEY = 'betbuds_quizzes';

function getStored() {
  const stored = localStorage.getItem(QUIZ_KEY);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(QUIZ_KEY, JSON.stringify([]));
  return [];
}

function save(data) {
  localStorage.setItem(QUIZ_KEY, JSON.stringify(data));
}

function dbWrite(promise) {
  promise.catch((err) => console.warn('[quiz] DynamoDB write failed:', err));
}

function quizToDb(quiz) {
  return {
    quizId: quiz.id,
    activityId: quiz.activityId,
    masterEventId: quiz.masterEventId,
    quizmasterId: quiz.quizmasterId,
    themes: quiz.themes || [],
    questions: JSON.stringify(quiz.questions || []),
    currentQuestionIdx: quiz.currentQuestionIdx,
    status: quiz.status,
    scoreboard: JSON.stringify(quiz.scoreboard || {}),
  };
}

// ===== Open Trivia DB integration =====
// https://opentdb.com/api_config.php - free, no API key needed, 4000+ questions

const OPENTDB_CATEGORIES = {
  'General Knowledge': 9,
  'Science & Nature': 17,
  'Sports': 21,
  'Geography': 22,
  'History': 23,
  'Movies & TV': 11,
  'Music': 12,
  'Food & Drink': null,
  'Kids': 9,
  'Norway': null,       // no API category, use local only
  'Pop Culture': 9,     // General Knowledge as proxy
  'Animals': 27,        // Animals category in OpenTDB
};

const DIFFICULTY_MAP = {
  'beginner': 'easy',
  'intermediate': 'medium',
  'pro': 'hard',
};

function decodeHtml(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

async function fetchFromOpenTDB(theme, count, difficulty) {
  const categoryId = OPENTDB_CATEGORIES[theme];
  const params = new URLSearchParams({ amount: String(count), type: 'multiple' });
  if (categoryId) params.set('category', String(categoryId));
  if (difficulty && DIFFICULTY_MAP[difficulty]) params.set('difficulty', DIFFICULTY_MAP[difficulty]);

  // Kids: force easy difficulty
  if (theme === 'Kids') params.set('difficulty', 'easy');

  try {
    const res = await fetch(`https://opentdb.com/api.php?${params.toString()}`);
    const data = await res.json();
    if (data.response_code === 0 && data.results?.length > 0) {
      return data.results.map((r) => ({
        q: decodeHtml(r.question),
        a: decodeHtml(r.correct_answer),
        d: difficulty || (r.difficulty === 'easy' ? 'beginner' : r.difficulty === 'medium' ? 'intermediate' : 'pro'),
        source: 'opentdb',
      }));
    }
  } catch (e) {
    // API failed, fall through to local fallback
  }
  return null;
}

// ===== Local fallback questions =====
// d: difficulty - 'beginner', 'intermediate', 'pro'
const THEME_SUGGESTIONS = {
  'Kids': [
    { q: 'What color is the sky on a sunny day?', a: 'Blue', d: 'beginner' },
    { q: 'How many legs does a spider have?', a: '8', d: 'beginner' },
    { q: 'What sound does a cow make?', a: 'Moo', d: 'beginner' },
    { q: 'What is the biggest animal in the ocean?', a: 'Blue whale', d: 'beginner' },
    { q: 'How many colors are in a rainbow?', a: '7', d: 'beginner' },
    { q: 'What do caterpillars turn into?', a: 'Butterflies', d: 'intermediate' },
    { q: 'Which planet do we live on?', a: 'Earth', d: 'beginner' },
    { q: 'What is frozen water called?', a: 'Ice', d: 'beginner' },
    { q: 'How many continents are there?', a: '7', d: 'intermediate' },
    { q: 'What is the fastest land animal?', a: 'Cheetah', d: 'intermediate' },
    { q: 'What is the largest bone in the human body?', a: 'Femur (thigh bone)', d: 'pro' },
    { q: 'What language has the most native speakers?', a: 'Mandarin Chinese', d: 'pro' },
  ],
  'General Knowledge': [
    { q: 'What is the largest planet in our solar system?', a: 'Jupiter', d: 'beginner' },
    { q: 'How many continents are there?', a: '7', d: 'beginner' },
    { q: 'What is the boiling point of water in Celsius?', a: '100°C', d: 'beginner' },
    { q: 'In which year did the Titanic sink?', a: '1912', d: 'intermediate' },
    { q: 'What is the chemical symbol for gold?', a: 'Au', d: 'intermediate' },
    { q: 'Which country has the most natural lakes?', a: 'Canada', d: 'intermediate' },
    { q: 'What is the speed of light in km/s (approximately)?', a: '300,000 km/s', d: 'pro' },
    { q: 'What is the only letter not appearing in any US state name?', a: 'Q', d: 'pro' },
    { q: 'How many time zones does Russia have?', a: '11', d: 'pro' },
  ],
  'Sports': [
    { q: 'How many players are on a football (soccer) team?', a: '11', d: 'beginner' },
    { q: 'In which sport would you perform a "slam dunk"?', a: 'Basketball', d: 'beginner' },
    { q: 'What color card means a player is sent off in football?', a: 'Red', d: 'beginner' },
    { q: 'Which country won the first FIFA World Cup in 1930?', a: 'Uruguay', d: 'intermediate' },
    { q: 'How many players are on a rugby union team?', a: '15', d: 'intermediate' },
    { q: 'Which tennis Grand Slam is played on clay?', a: 'French Open (Roland Garros)', d: 'intermediate' },
    { q: 'What is the maximum break in snooker?', a: '147', d: 'pro' },
    { q: 'Who holds the record for most F1 World Championships?', a: 'Michael Schumacher / Lewis Hamilton (7 each)', d: 'pro' },
    { q: 'In which year were the first modern Olympic Games held?', a: '1896', d: 'pro' },
  ],
  'Movies & TV': [
    { q: 'What is the name of the lion in "The Lion King"?', a: 'Simba', d: 'beginner' },
    { q: 'Which actor played Jack Sparrow?', a: 'Johnny Depp', d: 'beginner' },
    { q: 'What year was the first Star Wars film released?', a: '1977', d: 'beginner' },
    { q: 'Who directed "Pulp Fiction"?', a: 'Quentin Tarantino', d: 'intermediate' },
    { q: 'What is the highest-grossing film of all time?', a: 'Avatar', d: 'intermediate' },
    { q: 'In "The Office" (US), what is the name of the paper company?', a: 'Dunder Mifflin', d: 'intermediate' },
    { q: 'Which film won the first Academy Award for Best Picture?', a: 'Wings (1927)', d: 'pro' },
    { q: 'Who directed "2001: A Space Odyssey"?', a: 'Stanley Kubrick', d: 'pro' },
    { q: 'What was Alfred Hitchcock\'s last film?', a: 'Family Plot (1976)', d: 'pro' },
  ],
  'Science & Nature': [
    { q: 'What planet is known as the Red Planet?', a: 'Mars', d: 'beginner' },
    { q: 'What gas do plants absorb from the atmosphere?', a: 'Carbon dioxide (CO2)', d: 'beginner' },
    { q: 'What is the hardest natural substance on Earth?', a: 'Diamond', d: 'beginner' },
    { q: 'How many bones does an adult human have?', a: '206', d: 'intermediate' },
    { q: 'What is the powerhouse of the cell?', a: 'Mitochondria', d: 'intermediate' },
    { q: 'What is the most abundant gas in Earth\'s atmosphere?', a: 'Nitrogen (78%)', d: 'intermediate' },
    { q: 'What is the half-life of Carbon-14?', a: '~5,730 years', d: 'pro' },
    { q: 'What is the Chandrasekhar limit?', a: '~1.4 solar masses (max mass of a white dwarf)', d: 'pro' },
    { q: 'What particle was discovered at CERN in 2012?', a: 'Higgs boson', d: 'pro' },
  ],
  'Geography': [
    { q: 'What is the largest ocean on Earth?', a: 'Pacific Ocean', d: 'beginner' },
    { q: 'What is the capital of France?', a: 'Paris', d: 'beginner' },
    { q: 'Which river is the longest in the world?', a: 'The Nile', d: 'beginner' },
    { q: 'What is the smallest country in the world?', a: 'Vatican City', d: 'intermediate' },
    { q: 'In which country would you find Machu Picchu?', a: 'Peru', d: 'intermediate' },
    { q: 'What is the capital of Australia?', a: 'Canberra', d: 'intermediate' },
    { q: 'Which desert is the largest in the world?', a: 'Antarctic Desert', d: 'pro' },
    { q: 'What is the deepest point in the ocean?', a: 'Mariana Trench (Challenger Deep)', d: 'pro' },
    { q: 'Which country has the most UNESCO World Heritage Sites?', a: 'Italy', d: 'pro' },
  ],
  'Music': [
    { q: 'What instrument has 88 keys?', a: 'Piano', d: 'beginner' },
    { q: 'Who is known as the "King of Pop"?', a: 'Michael Jackson', d: 'beginner' },
    { q: 'Which band released "Bohemian Rhapsody"?', a: 'Queen', d: 'beginner' },
    { q: 'Which country does the band ABBA come from?', a: 'Sweden', d: 'intermediate' },
    { q: 'What is the best-selling album of all time?', a: 'Thriller by Michael Jackson', d: 'intermediate' },
    { q: 'What is the real name of Bono from U2?', a: 'Paul David Hewson', d: 'intermediate' },
    { q: 'Which classical composer became deaf later in life?', a: 'Ludwig van Beethoven', d: 'pro' },
    { q: 'What key is a standard guitar\'s lowest string tuned to?', a: 'E', d: 'pro' },
    { q: 'Who composed "The Four Seasons"?', a: 'Antonio Vivaldi', d: 'pro' },
  ],
  'Food & Drink': [
    { q: 'In which country did pizza originate?', a: 'Italy', d: 'beginner' },
    { q: 'What is the main ingredient of hummus?', a: 'Chickpeas', d: 'beginner' },
    { q: 'What fruit is used to make wine?', a: 'Grapes', d: 'beginner' },
    { q: 'Which country is the origin of the cocktail Mojito?', a: 'Cuba', d: 'intermediate' },
    { q: 'What is the most consumed beverage in the world after water?', a: 'Tea', d: 'intermediate' },
    { q: 'What type of pastry is used in a traditional eclair?', a: 'Choux pastry', d: 'intermediate' },
    { q: 'What is the Scoville scale used to measure?', a: 'Spiciness/heat of chili peppers', d: 'pro' },
    { q: 'Which country produces the most coffee in the world?', a: 'Brazil', d: 'pro' },
    { q: 'What Japanese word means "grilled chicken skewer"?', a: 'Yakitori', d: 'pro' },
  ],
  'History': [
    { q: 'Who was the first person to walk on the moon?', a: 'Neil Armstrong', d: 'beginner' },
    { q: 'In which year did World War II end?', a: '1945', d: 'beginner' },
    { q: 'Who painted the Mona Lisa?', a: 'Leonardo da Vinci', d: 'beginner' },
    { q: 'In which year did the Berlin Wall fall?', a: '1989', d: 'intermediate' },
    { q: 'Who painted the ceiling of the Sistine Chapel?', a: 'Michelangelo', d: 'intermediate' },
    { q: 'Which war ended with the Treaty of Versailles?', a: 'World War I', d: 'intermediate' },
    { q: 'What ancient wonder was located in Alexandria?', a: 'The Lighthouse (Pharos)', d: 'pro' },
    { q: 'Who was the last pharaoh of ancient Egypt?', a: 'Cleopatra VII', d: 'pro' },
    { q: 'In what year was the Magna Carta signed?', a: '1215', d: 'pro' },
  ],
  'Norway': [
    { q: 'What is the capital of Norway?', a: 'Oslo', d: 'beginner' },
    { q: 'What is the longest fjord in Norway?', a: 'Sognefjorden', d: 'beginner' },
    { q: 'What color is the Norwegian flag?', a: 'Red, white and blue', d: 'beginner' },
    { q: 'What is "brunost"?', a: 'Brown cheese (caramelized whey cheese)', d: 'beginner' },
    { q: 'What Norwegian city is known as the "Gateway to the Fjords"?', a: 'Bergen', d: 'beginner' },
    { q: 'What is the traditional Norwegian independence day?', a: '17th of May (May 17)', d: 'intermediate' },
    { q: 'What is the Norwegian word for "cheers" when toasting?', a: 'Skål', d: 'intermediate' },
    { q: 'Which Norwegian explorer reached the South Pole first?', a: 'Roald Amundsen', d: 'intermediate' },
    { q: 'What is the name of the famous Norwegian painting by Edvard Munch?', a: 'The Scream (Skrik)', d: 'intermediate' },
    { q: 'What Norwegian band had a hit with "Take On Me"?', a: 'a-ha', d: 'intermediate' },
    { q: 'What is the northernmost point of mainland Norway?', a: 'Nordkapp (North Cape)', d: 'intermediate' },
    { q: 'What is the traditional Norwegian Christmas dinner in western Norway?', a: 'Pinnekjøtt (dried lamb ribs)', d: 'intermediate' },
    { q: 'What is "russ" in Norway?', a: 'Graduating high school students who celebrate in spring with colored overalls', d: 'intermediate' },
    { q: 'Which Norwegian footballer played for Manchester United and was known as "The Baby-faced Assassin"?', a: 'Ole Gunnar Solskjær', d: 'pro' },
    { q: 'What year did Norway dissolve its union with Sweden?', a: '1905', d: 'pro' },
    { q: 'What is the name of the Norwegian sovereign wealth fund?', a: 'Government Pension Fund Global (Oljefondet)', d: 'pro' },
    { q: 'Which Norwegian author won the Nobel Prize in Literature in 1920?', a: 'Knut Hamsun', d: 'pro' },
    { q: 'What is the Sámi parliament in Norway called?', a: 'Sametinget', d: 'pro' },
    { q: 'In which Norwegian city was the 1994 Winter Olympics held?', a: 'Lillehammer', d: 'intermediate' },
    { q: 'What is "Kvikk Lunsj" and why is it associated with hiking?', a: 'A chocolate wafer bar, considered the hiking snack of Norway', d: 'intermediate' },
  ],
  'Pop Culture': [
    { q: 'What social media platform has a ghost as its logo?', a: 'Snapchat', d: 'beginner' },
    { q: 'What video game features a plumber named Mario?', a: 'Super Mario Bros', d: 'beginner' },
    { q: 'Who is the author of the Harry Potter series?', a: 'J.K. Rowling', d: 'beginner' },
    { q: 'What is the highest-grossing animated film franchise?', a: 'Frozen', d: 'intermediate' },
    { q: 'Which TV show features dragons and the Iron Throne?', a: 'Game of Thrones', d: 'intermediate' },
    { q: 'What is the name of Elon Musk\'s space company?', a: 'SpaceX', d: 'intermediate' },
    { q: 'In what year was the first iPhone released?', a: '2007', d: 'pro' },
    { q: 'What is the most-watched YouTube video of all time?', a: 'Baby Shark Dance', d: 'pro' },
    { q: 'What does NFT stand for?', a: 'Non-Fungible Token', d: 'pro' },
  ],
  'Animals': [
    { q: 'What is the largest land animal?', a: 'African elephant', d: 'beginner' },
    { q: 'How many legs does an octopus have?', a: '8', d: 'beginner' },
    { q: 'What is a group of wolves called?', a: 'A pack', d: 'beginner' },
    { q: 'What is the fastest bird in the world?', a: 'Peregrine falcon', d: 'intermediate' },
    { q: 'How long is an elephant\'s pregnancy?', a: 'About 22 months', d: 'intermediate' },
    { q: 'What is the only mammal that can truly fly?', a: 'Bat', d: 'intermediate' },
    { q: 'What animal has the highest blood pressure?', a: 'Giraffe', d: 'pro' },
    { q: 'How many hearts does an octopus have?', a: '3', d: 'pro' },
    { q: 'What is the loudest animal on Earth?', a: 'Sperm whale (up to 230 dB)', d: 'pro' },
  ],
};

const AVAILABLE_THEMES = Object.keys(THEME_SUGGESTIONS);
const DIFFICULTIES = ['beginner', 'intermediate', 'pro'];

export const quizService = {
  getAvailableThemes() {
    return AVAILABLE_THEMES;
  },

  getDifficulties() {
    return DIFFICULTIES;
  },

  // Sync fallback - uses local hardcoded questions only
  getSuggestedQuestions(theme, count = 5, difficulty = null) {
    let pool = THEME_SUGGESTIONS[theme] || [];
    if (difficulty) {
      pool = pool.filter((q) => q.d === difficulty);
    }
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  },

  // Async - tries OpenTDB API first, falls back to local
  async fetchSuggestedQuestions(theme, count = 5, difficulty = null) {
    const apiResult = await fetchFromOpenTDB(theme, count, difficulty);
    if (apiResult && apiResult.length > 0) {
      return apiResult;
    }
    // Fallback to local
    return this.getSuggestedQuestions(theme, count, difficulty);
  },

  // Get quiz state for an activity
  getQuiz(activityId) {
    return getStored().find((q) => q.activityId === activityId) || null;
  },

  // Create or reset quiz for an activity
  createQuiz({ activityId, masterEventId, quizmasterId }) {
    const quizzes = getStored();
    const existing = quizzes.findIndex((q) => q.activityId === activityId);
    const quiz = {
      id: existing >= 0 ? quizzes[existing].id : `quiz_${Date.now()}`,
      activityId,
      masterEventId,
      quizmasterId,
      themes: [],
      questions: [],
      currentQuestionIdx: -1, // -1 = not started
      status: 'setup', // setup | active | finished
      scoreboard: {}, // userId -> { points, adjustments: [{amount, reason}] }
      createdAt: new Date().toISOString(),
    };
    if (existing >= 0) quizzes[existing] = quiz;
    else quizzes.push(quiz);
    save(quizzes);

    // DynamoDB dual-write
    if (existing >= 0) {
      dbWrite(db.updateQuiz(quiz.id, quizToDb(quiz)));
    } else {
      dbWrite(db.createQuiz(quizToDb(quiz)));
    }

    return quiz;
  },

  updateQuiz(activityId, updates) {
    const quizzes = getStored();
    const idx = quizzes.findIndex((q) => q.activityId === activityId);
    if (idx === -1) throw new Error('Quiz not found');
    Object.assign(quizzes[idx], updates);
    save(quizzes);

    // DynamoDB dual-write
    const dbUpdates = {};
    if (updates.themes !== undefined) dbUpdates.themes = updates.themes;
    if (updates.questions !== undefined) dbUpdates.questions = JSON.stringify(updates.questions);
    if (updates.currentQuestionIdx !== undefined) dbUpdates.currentQuestionIdx = updates.currentQuestionIdx;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.scoreboard !== undefined) dbUpdates.scoreboard = JSON.stringify(updates.scoreboard);
    if (updates.quizmasterId !== undefined) dbUpdates.quizmasterId = updates.quizmasterId;
    if (Object.keys(dbUpdates).length > 0) {
      dbWrite(db.updateQuiz(quizzes[idx].id, dbUpdates));
    }

    return quizzes[idx];
  },

  // Theme management
  addTheme(activityId, themeName) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    if (!quiz.themes.includes(themeName)) {
      quiz.themes.push(themeName);
      return this.updateQuiz(activityId, { themes: quiz.themes });
    }
    return quiz;
  },

  removeTheme(activityId, themeName) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    quiz.themes = quiz.themes.filter((t) => t !== themeName);
    return this.updateQuiz(activityId, { themes: quiz.themes });
  },

  // Question management
  addQuestion(activityId, { question, answer, theme, points }) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    quiz.questions.push({
      id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      question,
      answer: answer || '',
      theme: theme || 'General',
      points: points || 1,
      status: 'pending', // pending | active | answered
      winnerId: null,
      awardedPoints: null,
    });
    return this.updateQuiz(activityId, { questions: quiz.questions });
  },

  removeQuestion(activityId, questionId) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    quiz.questions = quiz.questions.filter((q) => q.id !== questionId);
    return this.updateQuiz(activityId, { questions: quiz.questions });
  },

  addSuggestedQuestions(activityId, theme, count = 5, difficulty = null) {
    const suggestions = this.getSuggestedQuestions(theme, count, difficulty);
    let quiz = this.getQuiz(activityId);
    suggestions.forEach((s) => {
      quiz = this.addQuestion(activityId, { question: s.q, answer: s.a, theme, points: 1 });
    });
    return quiz;
  },

  // Quiz flow
  startQuiz(activityId) {
    const quiz = this.getQuiz(activityId);
    if (!quiz || quiz.questions.length === 0) throw new Error('Add questions before starting');
    return this.updateQuiz(activityId, { status: 'active', currentQuestionIdx: 0 });
  },

  activateQuestion(activityId, questionIdx) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    quiz.questions.forEach((q, i) => {
      if (i === questionIdx && q.status === 'pending') q.status = 'active';
    });
    return this.updateQuiz(activityId, { questions: quiz.questions, currentQuestionIdx: questionIdx });
  },

  awardQuestion(activityId, questionId, winnerId, points) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    const qIdx = quiz.questions.findIndex((q) => q.id === questionId);
    if (qIdx === -1) throw new Error('Question not found');

    const awardedPts = points !== undefined ? points : quiz.questions[qIdx].points;
    quiz.questions[qIdx].status = 'answered';
    quiz.questions[qIdx].winnerId = winnerId;
    quiz.questions[qIdx].awardedPoints = awardedPts;

    // Update scoreboard
    if (!quiz.scoreboard[winnerId]) quiz.scoreboard[winnerId] = { points: 0, adjustments: [] };
    quiz.scoreboard[winnerId].points += awardedPts;

    return this.updateQuiz(activityId, { questions: quiz.questions, scoreboard: quiz.scoreboard });
  },

  skipQuestion(activityId, questionId) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    const q = quiz.questions.find((x) => x.id === questionId);
    if (q) q.status = 'answered'; // mark as done, no winner
    return this.updateQuiz(activityId, { questions: quiz.questions });
  },

  // Adjust points (bonus or penalty)
  adjustPoints(activityId, userId, amount, reason) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');
    if (!quiz.scoreboard[userId]) quiz.scoreboard[userId] = { points: 0, adjustments: [] };
    quiz.scoreboard[userId].points += amount;
    quiz.scoreboard[userId].adjustments.push({ amount, reason, at: new Date().toISOString() });
    return this.updateQuiz(activityId, { scoreboard: quiz.scoreboard });
  },

  changeQuizmaster(activityId, newQuizmasterId) {
    return this.updateQuiz(activityId, { quizmasterId: newQuizmasterId });
  },

  async finishQuiz(activityId) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) throw new Error('Quiz not found');

    // Push scores to master event scoring
    const rankings = Object.entries(quiz.scoreboard)
      .map(([userId, data]) => ({ userId, points: data.points }))
      .sort((a, b) => b.points - a.points)
      .map((entry, idx) => ({ ...entry, placement: idx + 1 }));

    if (rankings.length > 0) {
      await scoringService.setScores(quiz.masterEventId, activityId, rankings);

      // Post results to feed
      const allUsers = authService.getAllUsers();
      const winner = rankings[0] ? allUsers.find((u) => u.id === rankings[0].userId) : null;
      const runnerUp = rankings[1] ? allUsers.find((u) => u.id === rankings[1].userId) : null;
      let content = `🏆 Quiz finished! ${quiz.questions.length} questions played.`;
      if (winner) content += ` Winner: ${winner.avatar} ${winner.displayName} (${rankings[0].points} pts)`;
      if (runnerUp) content += ` | Runner-up: ${runnerUp.avatar} ${runnerUp.displayName} (${rankings[1].points} pts)`;
      feedService.post({
        masterEventId: quiz.masterEventId,
        activityId,
        type: 'quiz_result',
        userId: quiz.quizmasterId,
        content,
        metadata: { rankings: rankings.slice(0, 5) },
      }).catch((err) => console.warn('[quiz] Could not post quiz result to feed:', err));
    }

    return this.updateQuiz(activityId, { status: 'finished' });
  },

  // Get sorted scoreboard
  getScoreboard(activityId) {
    const quiz = this.getQuiz(activityId);
    if (!quiz) return [];
    return Object.entries(quiz.scoreboard)
      .map(([userId, data]) => ({ userId, ...data }))
      .sort((a, b) => b.points - a.points);
  },
};
